import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchLoyalty,
  loadMoreLoyaltyHistory,
  redeemLoyaltyPoints,
  requestReferralCode,
  uploadLoyaltyPhotoThunk,
} from '../store/slices/loyaltySlice';
import { RedeemResult } from '../services/loyalty';

/**
 * GT Pontok a bejelentkezett felhasználónak — közös (Redux) állapotból, így a
 * fejléc-badge és a Fiók pont-panelje együtt frissül. Nem hívja a végpontot, ha
 * nincs bejelentkezve. `refreshOnMount`: a hívó (pl. a Fiók oldal) friss adatot kér.
 * A műveletek hibánál a felületnek szánt magyar üzenettel (Error) dobnak.
 */
export function useLoyalty({ refreshOnMount = false }: { refreshOnMount?: boolean } = {}) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const { data, loading, error, loadingMore } = useAppSelector((s) => s.loyalty);
  // Ref: a betöltés (data megjelenése) ne indítson újabb lekérést az effektben.
  const hasDataRef = useRef(data !== null);
  hasDataRef.current = data !== null;

  const refetch = useCallback(() => {
    if (isAuthenticated) dispatch(fetchLoyalty());
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (refreshOnMount || !hasDataRef.current) dispatch(fetchLoyalty());
  }, [dispatch, isAuthenticated, refreshOnMount]);

  const redeem = useCallback(
    async (points: number): Promise<RedeemResult> => {
      const res = await dispatch(redeemLoyaltyPoints(points));
      if (redeemLoyaltyPoints.fulfilled.match(res)) return res.payload;
      throw new Error(res.payload ?? 'Nem sikerült beváltani a pontokat.');
    },
    [dispatch],
  );

  const uploadPhoto = useCallback(
    async (file: File, caption = '') => {
      const res = await dispatch(uploadLoyaltyPhotoThunk({ file, caption }));
      if (uploadLoyaltyPhotoThunk.fulfilled.match(res)) return res.payload;
      throw new Error(res.payload ?? 'A fotó feltöltése nem sikerült.');
    },
    [dispatch],
  );

  const createReferral = useCallback(async () => {
    const res = await dispatch(requestReferralCode());
    if (requestReferralCode.fulfilled.match(res)) return res.payload;
    throw new Error(res.payload ?? 'Nem sikerült létrehozni az ajánló linket.');
  }, [dispatch]);

  const loadMoreHistory = useCallback(() => {
    dispatch(loadMoreLoyaltyHistory());
  }, [dispatch]);

  return {
    data, loading, error, loadingMore, refetch, redeem, uploadPhoto, createReferral, loadMoreHistory,
    isAuthenticated,
  };
}
